import { Components, CSSObject, Theme } from '@mui/material/styles';
import { gridClasses } from '@mui/x-data-grid-pro';
import scssVariables from '@styles/variables.module.scss';

export const dataGridCellFontFamilyTabularNumsClassName =
  'MuiDataGrid-cell--tabular-nums';

export const DATAGRID_CUSTOM_CLASS_NAMES = {
  ROW_ERROR: 'Error',
  ROW_WARNING: 'Warning',
  ROW_SUCCESS: 'Success',
  ROW_SELECTED: 'RowSelected',
  ROW_TREE_LEAF: 'TreeLeaf',
};

export const MuiDataGrid: Components<Theme>['MuiDataGrid'] = {
  defaultProps: {
    rowHeight: 56,
    columnHeaderHeight: 44,

    classes: {
      scrollbar: 'MuiCustomScrollbarHelper',
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderWidth: 1,
      borderColor: scssVariables.ColorFog40,
      borderRadius: 'var(--border-radius-x-large)',
      '--rowBorderColor': theme.vars
        ? theme.vars.palette.divider
        : theme.palette.divider,

      '.MuiDataGrid-columnSeparator': {
        opacity: 0,
        transition: 'opacity 0.2s',
      },

      '.MuiDataGrid-scrollShadow': {
        borderTopLeftRadius: '0',
        borderBottomLeftRadius: '0',
      },
      '.MuiDataGrid-treeDataGroupingCellToggle': {
        marginRight: theme.spacing(0.5),
      },
      '.MuiDataGrid-treeDataGroupingCellToggle:empty': {
        flexBasis: 'var(--input-height-small)',
      },

      ...theme.applyStyles('dark', {
        borderColor: scssVariables.ColorFog40,
      }),
      variants: [
        {
          props: { hasRowNavigation: true },
          style: ({ theme }) => ({
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within, & .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
              {
                outline: 'none',
              },

            [`& .${gridClasses.row}:focus-within:not(:has(a:focus)):not(:has(button:focus))`]:
              {
                backgroundColor: scssVariables.ColorFog20,
                ...theme.applyStyles('dark', {
                  backgroundColor: scssVariables.ColorFog100,
                }),

                '.MuiDataGrid-cell': {
                  backgroundColor: 'inherit',
                },
              },
          }),
        },
      ],
    }),

    columnHeaders: ({ theme }) => ({
      '&:hover .MuiDataGrid-columnSeparator': {
        opacity: 1,
      },
      // bump specificity to override default styles
      '&.MuiDataGrid-columnHeaders': {
        ...theme.applyStyles('dark', {
          backgroundColor: scssVariables.ColorBlack100,
        }),
        ...theme.applyStyles('light', {
          backgroundColor: scssVariables.ColorBlack00,
        }),
      },
      // bump specificity to override default styles
      '.MuiDataGrid-columnHeader': {
        ...theme.applyStyles('dark', {
          backgroundColor: scssVariables.ColorBlack100,
        }),
        ...theme.applyStyles('light', {
          backgroundColor: scssVariables.ColorBlack00,
        }),
      },
    }),

    columnHeaderTitle: ({ theme }): CSSObject => ({
      ...(theme.typography.label as CSSObject),
      fontWeight: 400,
      ...theme.applyStyles('dark', {
        color: scssVariables.ColorBlack00,
      }),
      ...theme.applyStyles('light', {
        color: scssVariables.ColorBlack60,
      }),
    }),

    row: ({ theme }) => ({
      // Base styles (apply to both modes)
      '&:not(:first-of-type) .MuiDataGrid-cell': {
        borderTopWidth: 1,
        borderTopStyle: 'solid',
      },

      '&:hover': {
        '.MuiDataGrid-cell': {
          backgroundColor: 'inherit',
        },
      },

      // Dark mode styles
      ...theme.applyStyles('dark', {
        backgroundColor: scssVariables.ColorBlack100,

        '&:hover': {
          backgroundColor: scssVariables.ColorBlack80,
        },

        '&:not(:first-of-type) .MuiDataGrid-cell': {
          borderTopColor: scssVariables.ColorBlack60,
        },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_TREE_LEAF}:not(:first-of-type) .MuiDataGrid-cell`]:
          {
            borderTopColor: scssVariables.ColorBlack40,
          },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_ERROR}`]: {
          backgroundImage: `linear-gradient(165deg, ${scssVariables.ColorError100} 5.28%, ${scssVariables.ColorBlack100} 62.1%)`,
        },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_WARNING}`]: {
          backgroundImage: `linear-gradient(165deg, ${scssVariables.ColorWarning100} 5.28%, ${scssVariables.ColorBlack100} 62.1%)`,
        },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_SUCCESS}`]: {
          backgroundImage: `linear-gradient(165deg, ${scssVariables.ColorSuccess100} 5.28%, ${scssVariables.ColorBlack100} 62.1%)`,
        },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_SELECTED}`]: {
          backgroundColor: scssVariables.ColorFog100,
        },
      }),

      // Light mode styles
      ...theme.applyStyles('light', {
        backgroundColor: scssVariables.ColorBlack00,

        '&:hover': {
          backgroundColor: scssVariables.ColorFog10,
        },

        '&:not(:first-of-type) .MuiDataGrid-cell': {
          borderTopColor: scssVariables.ColorFog40,
        },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_TREE_LEAF}:not(:first-of-type) .MuiDataGrid-cell`]:
          {
            borderTopColor: scssVariables.ColorFog20,
          },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_ERROR}`]: {
          backgroundImage: `linear-gradient(165deg, ${scssVariables.ColorError10} 5.28%, ${scssVariables.ColorBlack00} 62.1%)`,
        },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_WARNING}`]: {
          backgroundImage: `linear-gradient(165deg, ${scssVariables.ColorWarning10} 5.28%, ${scssVariables.ColorBlack00} 62.1%)`,
        },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_SUCCESS}`]: {
          backgroundImage: `linear-gradient(165deg, ${scssVariables.ColorSuccess10} 5.28%, ${scssVariables.ColorBlack00} 62.1%)`,
        },
        [`&.${DATAGRID_CUSTOM_CLASS_NAMES.ROW_SELECTED}`]: {
          backgroundColor: scssVariables.ColorFog10,
        },
      }),
    }),
    columnHeader: ({ theme }) => ({
      padding: `0 ${theme.spacing(2)}`,

      '&.MuiDataGrid-columnHeaderCheckbox': {
        padding: 0,
      },
    }),
    cell: ({ theme }) => ({
      padding: `0 ${theme.spacing(2)}`,

      a: {
        color: scssVariables.ColorNavy100,

        '&:hover': {
          textDecoration: 'underline',
        },
      },

      [`&.${dataGridCellFontFamilyTabularNumsClassName}`]: {
        fontVariantNumeric: 'tabular-nums',
      },

      '&.MuiDataGrid-cellCheckbox': {
        padding: 0,
      },
    }),
    'cell--pinnedLeft': {
      backgroundColor: 'inherit',
    },
    'cell--pinnedRight': {
      backgroundColor: 'inherit',
    },
    sortButton: ({ theme }) => ({
      marginLeft: theme.spacing(0.5),
      width: 'var(--input-height-tiny)',
      height: 'var(--input-height-tiny)',
    }),
    footerCell: () => ({
      fontWeight: scssVariables.FontWeightBodyBold, // use weight directly, as we need to keep the line-height intact for tetxt centering
      color: scssVariables.ColorFog100,
    }),
  },
};
